import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCameraOutline, mdiWaterAlert  } from '@mdi/js'
import { Button } from '../ui/button'
import { EntityPicture } from '../EntityPicture'
import { DeclareIssueDialog } from '../issues/DeclareIssueDialog'
import { queryKeys } from '../../hooks/useQueries'
import { SECTION_ICONS } from '../../lib/section-icons'

type Props = {
    org: string
    assetUid: string
    /** Only used to pre-fill the ticket title — the name itself is already the
     * page header, right above this tab. */
    assetName: string
    /** Current picture document uid — the cache-buster for the picture URL. */
    pictureDocumentUid?: string
}

/**
 * The Photo tab of an equipment / building-element asset.
 *
 * This is not a gallery. It is the first screen a resident sees after
 * photographing the QR sticker glued on a boiler, a lift or a hall door, so it
 * answers two questions in that order: *is this the thing I am standing in
 * front of* (the picture), and *what can I do about it* (the two actions).
 *
 * Deliberately nothing else. The name, the type and the location all already
 * sit in the page header a few pixels above; repeating them here only pushed
 * the two buttons off a phone screen. On a 390 px viewport every pixel between
 * the top of the tab and the buttons is a pixel the resident has to scroll
 * past, so the tab holds a picture and two targets, and that is all.
 *
 * The picture is 4:3 rather than square for the same reason — a square eats
 * 390 px of an 844 px screen, and `object-cover` crops a portrait phone shot
 * to fit either way.
 */
export function AssetPhotoTab({ org, assetUid, assetName, pictureDocumentUid }: Props) {
    const { t } = useTranslation('assets')
    const queryClient = useQueryClient()
    // Which declaration the dialog is currently composing, or null when closed.
    // The two actions differ only by the ticket they open, so they share it.
    const [declaring, setDeclaring] = useState<'damage' | 'breakdown' | null>(null)

    const invalidateAsset = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, assetUid) })
    }

    return (
        <div data-testid="asset-photo-tab">
            <div className="mx-auto max-w-md space-y-3 rounded-lg border border-border bg-card p-3 sm:p-4">
                {/* No separate "add a picture" button: tapping the empty frame
                    already opens the picker, and a second control just to say so
                    cost a full row above the fold. */}
                <EntityPicture
                    entityType="asset"
                    entityId={assetUid}
                    orgCode={org}
                    // 4:3 to match the frame. A square thumbnail would be
                    // cropped square by vipsthumbnail and then cropped again to
                    // 4:3 by `object-cover` — two crops, twice the loss.
                    size="768x576"
                    documentUid={pictureDocumentUid}
                    editable
                    className="w-full aspect-[4/3] rounded-lg"
                    alt={t('photo.alt', { name: assetName, defaultValue: 'Photo de {{name}}' })}
                    // A camera, which reads as "there could be one here",
                    // rather than the generic wrench.
                    fallbackIcon={mdiCameraOutline}
                    onUpload={(success, error) => {
                        if (success) invalidateAsset()
                        else console.error('Failed to update asset picture:', error)
                    }}
                />

                {/* The two things a resident standing here can actually do.
                    Stacked and full-width on a phone, 48 px tall so a thumb
                    cannot miss; side by side only once there is room. */}
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                        size="lg"
                        className="h-12 w-full shrink-0 text-base sm:h-11 sm:flex-1 sm:text-sm"
                        onClick={() => setDeclaring('damage')}
                        data-testid="asset-photo-declare-damage"
                    >
                        <AppIcon path={mdiWaterAlert} size="md" className="mr-2 shrink-0" />
                        {t('photo.declare_damage')}
                    </Button>
                    <Button
                        size="lg"
                        variant="outline"
                        className="h-12 w-full shrink-0 text-base sm:h-11 sm:flex-1 sm:text-sm"
                        onClick={() => setDeclaring('breakdown')}
                        data-testid="asset-photo-report-breakdown"
                    >
                        <AppIcon path={SECTION_ICONS.interventions} size="md" className="mr-2 shrink-0" />
                        {t('photo.report_breakdown')}
                    </Button>
                </div>
            </div>

            {/* One declaration surface for both: same questions, different
                ticket. A dégât opens an insurance claim, a panne a service
                ticket — the declarer is never asked to make that call. */}
            <DeclareIssueDialog
                open={declaring !== null}
                onOpenChange={(open) => { if (!open) setDeclaring(null) }}
                kind={declaring === 'breakdown' ? 'breakdown' : 'damage'}
                targetType="asset"
                targetEntityUid={assetUid}
                assetUid={assetUid}
                subjectName={assetName}
                declaredFrom="photo_tab"
            />
        </div>
    )
}

